import { Switch, Route } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "next-themes";
import NotFound from "@/pages/not-found";
import LandingPage from "@/pages/landing-page";
import AuthPage from "@/pages/auth-page";
import ForgotPasswordPage from "@/pages/forgot-password-page";
import DashboardPage from "@/pages/dashboard-page";
import BillingPage from "@/pages/billing-page";
import SettingsPage from "@/pages/settings-page";
import ShowcasePage from "@/pages/showcase-page";
import FaqPage from "@/pages/faq-page";
import PrivacyPage from "@/pages/privacy-page";
import TermsPage from "@/pages/terms-page";
import ScreenplayPage from "@/pages/screenplay-page";
import RenderPage from "@/pages/render-page";
import PrepRenderPage from "@/pages/prep-render-page";
import PricingPage from "@/pages/pricing-page";
import { ProtectedRoute } from "@/lib/protected-route";
import { AuthProvider } from "@/hooks/use-auth";
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect } from "react";
import { useLocation } from "wouter"; // Import wouter's useLocation for navigation

function Router() {
  return (
    <Switch>
      {/* Public routes */}
      <Route path="/" component={LandingPage} />
      <Route path="/auth" component={AuthPage} />
      <Route path="/forgot-password" component={ForgotPasswordPage} />
      <Route path="/showcase" component={ShowcasePage} />
      <Route path="/pricing" component={PricingPage} />
      <Route path="/faq" component={FaqPage} />
      <Route path="/privacy" component={PrivacyPage} />
      <Route path="/terms" component={TermsPage} />

      {/* Protected routes */}
      <ProtectedRoute path="/dashboard" component={DashboardPage} />
      <ProtectedRoute path="/billing" component={BillingPage} />
      <ProtectedRoute path="/settings" component={SettingsPage} />
      <ProtectedRoute path="/screenplays/:id" component={ScreenplayPage} />
      <ProtectedRoute path="/renders/:id" component={RenderPage} />
      <ProtectedRoute path="/prep-render/:id" component={PrepRenderPage} />

      {/* Fallback to 404 */}
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  const { isAuthenticated, getIdTokenClaims, isLoading } = useAuth0();
  const [location , navigate] = useLocation();

  useEffect(() => {
    const isAuthRoute =
    location === "/" || location === "" ||
    location === "/auth" || location === "/auth#register";

    async function exchangeToken() {
      try {
        const tokenClaims = await getIdTokenClaims();
        const idToken = tokenClaims?.__raw;
        if (!idToken) {
          console.warn("No ID token found for exchange.");
          return;
        }

        const response = await fetch("/api/auth/token-exchange", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idToken }),
          credentials: "include", // Ensures session cookie is sent
        });

        const data = await response.json();
        if (response.ok) {
          console.log("Token exchange successful, session established.");
          queryClient.invalidateQueries({ queryKey: ["/api/user"] }); //refresh user
          if (isAuthRoute) {
            navigate("/dashboard");
          }
        } else {
          console.error("Token exchange failed:", data.error);
        }
      } catch (err) {
        console.error("Error during token exchange:", err);
      }
    }

    if (isAuthenticated && !isLoading) {
      exchangeToken();
    }
  }, [isAuthenticated, isLoading, getIdTokenClaims]);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="light">
        <TooltipProvider>
          <AuthProvider>
            <Toaster />
            <Router />
          </AuthProvider>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
